import { defineStore } from 'pinia'
import { cartLinesSubtotal } from '@/lib/menuOrderBuilder'
import { parseItemPrice } from '@/lib/restaurantPublicProfile'

function lineKey(itemId, sizeLabel) {
  return `${itemId}::${sizeLabel || ''}`
}

export const useMenuCartStore = defineStore('menuCart', {
  state: () => ({
    restaurantId: '',
    restaurantName: '',
    restaurantWhere: '',
    lines: []
  }),

  getters: {
    isEmpty: (s) => !s.lines.length,
    itemCount: (s) => s.lines.reduce((n, l) => n + (parseInt(l.qty, 10) || 1), 0),
    subtotal: (s) => cartLinesSubtotal(s.lines)
  },

  actions: {
    clear() {
      this.restaurantId = ''
      this.restaurantName = ''
      this.restaurantWhere = ''
      this.lines = []
    },

    ensureRestaurant({ id, name_ar, name, where }) {
      const rid = String(id || '').trim()
      if (!rid) return false
      if (this.restaurantId && this.restaurantId !== rid) {
        this.clear()
      }
      this.restaurantId = rid
      this.restaurantName = String(name_ar || name || '').trim()
      this.restaurantWhere = String(where || '').trim()
      return true
    },

    addLine({
      restaurant,
      itemId,
      itemName,
      qty = 1,
      sizeLabel = '',
      unitPrice,
      imageUrl = '',
      notes = ''
    }) {
      if (!this.ensureRestaurant(restaurant)) return
      const key = lineKey(itemId, sizeLabel)
      const price = parseItemPrice(unitPrice)
      const img = String(imageUrl || '').trim()
      const existing = this.lines.find((l) => l.key === key)
      if (existing) {
        existing.qty = (parseInt(existing.qty, 10) || 1) + (parseInt(qty, 10) || 1)
        if (img && !existing.imageUrl) existing.imageUrl = img
        return
      }
      this.lines.push({
        key,
        itemId: String(itemId),
        itemName: String(itemName || '').trim(),
        qty: Math.max(1, parseInt(qty, 10) || 1),
        sizeLabel: String(sizeLabel || '').trim(),
        unitPrice: price,
        imageUrl: img,
        notes: String(notes || '').trim()
      })
    },

    setQty(key, qty) {
      const line = this.lines.find((l) => l.key === key)
      if (!line) return
      const q = parseInt(qty, 10)
      if (!q || q < 1) {
        this.removeLine(key)
        return
      }
      line.qty = q
    },

    removeLine(key) {
      this.lines = this.lines.filter((l) => l.key !== key)
      if (!this.lines.length) {
        this.restaurantId = ''
        this.restaurantName = ''
        this.restaurantWhere = ''
      }
    }
  }
})

/**
 * Keep the cart in the browser, so a reload or a phone reopening the tab does
 * not empty it (found in the 17 October 2026 review: add an item, refresh, «السلة
 * فارغة»). Saved for two days; a cart older than that starts empty.
 */
const CART_STORAGE_KEY = 'restora_cart_v1'
const CART_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000

export function menuCartPersistence({ store }) {
  if (store.$id !== 'menuCart' || typeof localStorage === 'undefined') return
  try {
    const saved = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || 'null')
    if (saved && Array.isArray(saved.lines) && Date.now() - (saved.savedAt || 0) < CART_MAX_AGE_MS) {
      store.$patch({
        restaurantId: saved.restaurantId || '',
        restaurantName: saved.restaurantName || '',
        restaurantWhere: saved.restaurantWhere || '',
        lines: saved.lines
      })
    }
  } catch {
    /* a broken saved cart is ignored */
  }
  store.$subscribe(
    (_mutation, state) => {
      try {
        if (!state.lines.length) localStorage.removeItem(CART_STORAGE_KEY)
        else localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }))
      } catch {
        /* storage full or blocked: the cart still works in memory */
      }
    },
    { detached: true }
  )
}
